import type { SceneGroup } from '../core/SceneGroup';
import type { Scene } from '../core/scene/Scene';
import { Config } from '../video-data/config';
import { Volumes } from './types';
import type { AudioAsset, AudioSourceData } from './AudioAsset';

export type AudioAnimationManagerSettings = {
    getVoiceover: () => string | AudioSourceData;
    getVoiceoverAsset: () => AudioAsset;
    getMusicAsset: () => AudioAsset;
    getSfxVolume: () => number;
    getMusicId: () => string;
    getVoiceoverId: () => string;
    getAudioAssets: () => Record<string, AudioAsset>;
    getSceneGroups: () => readonly SceneGroup[];
};

enum TRANSITION_HAPPENS_IN {
    NO_TRANSITION = 'no transition',
    FIRST = 'first',
    SECOND = 'second',
}

export const QUICK_TRANSITION_DURATION = 10.0; // in milliseconds
const LONG_TRANSITION_DURATION = Config.AUDIO_TRANSITION_DURATION;

type SteadyStateVolumeOptions = {
    transitionsShouldHappenOutsideSpan?: boolean;
    isAudioLocal?: boolean; // Is audio local to scene or sceneGroup.
    asset?: AudioAsset;
    /** Custom duration to use instead of scene/sceneGroup duration */
    duration?: number;
};

class SteadyStateVolume {
    volume!: number;
    spanBeginningTime!: number;
    spanEndTime!: number;
    asset?: AudioAsset = undefined;

    /**
     * This property is useful to indicate scenes where we'd like the transitions in
     * and out of the steady state to happen OUTSIDE of the scene.
     *
     * For example, we want background audio to duck down if there is a scene with a
     * MediaAsset with audio on it. In this case, we want the background audio to duck
     * BEFORE the scene, and then increase again to a normal volume AFTER the scene. In
     * this case, we should set transitionShouldHappenOutsideSpan to true.
     */
    transitionsShouldHappenOutsideSpan!: boolean;

    constructor(
        volume: number,
        sceneOrSceneGroup: Scene | SceneGroup,
        options: SteadyStateVolumeOptions = {},
    ) {
        this.volume = volume;
        if (options.isAudioLocal === true) {
            // if the audio is local to the slide, we use the local time
            // "coordinates" rather than the global time "coordinates"
            this.spanBeginningTime = 0;
            this.spanEndTime = options.duration ?? sceneOrSceneGroup.duration;
        } else {
            this.spanBeginningTime = sceneOrSceneGroup.startTime;
            this.spanEndTime =
                options.duration !== undefined
                    ? sceneOrSceneGroup.startTime + options.duration
                    : sceneOrSceneGroup.endTime;
        }
        this.transitionsShouldHappenOutsideSpan =
            options.transitionsShouldHappenOutsideSpan || false;
        this.asset = options.asset || undefined;
    }

    get spanMidTime() {
        return (this.spanBeginningTime + this.spanEndTime) / 2.0;
    }
}

export class VolumePoint {
    volume!: number;
    time!: number;

    constructor(volume: number, time: number) {
        this.volume = volume;
        this.time = time;
    }
}

type AssetTransitionPoints = { asset: AudioAsset; transitionPoints: VolumePoint[] };

/**
 * The AudioAnimationManager is used to compute/re-computer the transitionPoints for all the
 * AudioAssets that exist inside of a luminary instance.
 *
 * There are two stages to this computation:
 *
 * 1. Calculate steady-state volumes for every AudioAsset on every Scene. For example,
 * we want to duck the background track volume if there is a scene with a MediaAsset with audio.
 * These steady-state volumes are stored as a series of SceneSteadyStateVolume objects.
 *
 * Sample Video
 *      - X marks dictate where the SceneSteadyStateVolume points are
 *
 *
 * 100% +        X                                        X
 *      |      scene 1             scene 2             scene 3
 *      |    100% volume         15% volume          100% volume
 *      |
 *  15% +                            X
 *      |-------------------|-------------------|------------------|
 *
 * 2. Create audio transition curves (stored as a series of VolumePoints) from the SceneSteadyStateVolumes.
 * Most significantly, this includes computing where the transitions should start/end so that the steady-state
 * volume gets hit within each scene, and there are no conflicts between a transition to a steady-state volume,
 * and a transition out of that same steady-state volume.
 *
 * Sample Video (same as above)
 *      - X characters dictate where the SceneSteadyStateVolume points are
 *      - O characters dictate where the transitionPoints are
 *      - line indicates the volume curve, connecting all the 0 characters
 *
 * 100% O--------X-----O                              O-----X------O
 *      |      scene 1  \           scene 2         /     scene 3
 *      |    100% volume \        15% volume       /    100% volume
 *      |                 \                       /
 *  15% +                   O--------X----------O
 *      O-------------------|-------------------|------------------O
 *
 */
export class AudioAnimationManager {
    #getVoiceover: () => string | AudioSourceData;
    #getVoiceoverAsset: () => AudioAsset;
    #getMusicAsset: () => AudioAsset;
    #getSfxVolume: () => number;
    #getMusicId: () => string;
    #getVoiceoverId: () => string;
    #getAudioAssets: () => Record<string, AudioAsset>;
    #getSceneGroups: () => readonly SceneGroup[];

    constructor(settings: AudioAnimationManagerSettings) {
        this.#getVoiceover = settings.getVoiceover;
        this.#getVoiceoverAsset = settings.getVoiceoverAsset;
        this.#getMusicAsset = settings.getMusicAsset;
        this.#getSfxVolume = settings.getSfxVolume;
        this.#getMusicId = settings.getMusicId;
        this.#getVoiceoverId = settings.getVoiceoverId;
        this.#getAudioAssets = settings.getAudioAssets;
        this.#getSceneGroups = settings.getSceneGroups;
    }

    _getMediaAssetSteadyStateVolumesForScene(scene: Scene): SteadyStateVolume[] {
        return scene
            .getAssetsByType(Config.MEDIA_ASSET_TYPE)
            .filter((mediaAsset) => mediaAsset.hasAudio)
            .map((mediaAsset) => {
                return new SteadyStateVolume(mediaAsset.volume, scene, {
                    asset: mediaAsset._audioAsset,
                    isAudioLocal: true,
                });
            });
    }

    _getMediaAssetSteadyStateVolumesForSceneGroup(sceneGroup: SceneGroup): SteadyStateVolume[] {
        const mediaAsset = sceneGroup.mediaAsset;
        if (mediaAsset?.hasAudio) {
            return [
                new SteadyStateVolume(mediaAsset.volume, sceneGroup, {
                    asset: mediaAsset._audioAsset,
                    isAudioLocal: true,
                }),
            ];
        }
        return [];
    }

    _getVoiceoverAssetSteadyStateVolumesForSceneGroup(sceneGroup: SceneGroup): SteadyStateVolume[] {
        const voiceoverAsset = sceneGroup.voiceoverAsset;
        if (voiceoverAsset) {
            return [
                new SteadyStateVolume(voiceoverAsset.volume, sceneGroup, {
                    asset: voiceoverAsset,
                    isAudioLocal: true,
                }),
            ];
        }
        return [];
    }

    _getVoiceoverSteadyStateVolumeForScene(scene: Scene): SteadyStateVolume {
        if (this.#getVoiceover()) {
            return new SteadyStateVolume(this.#getVoiceoverAsset().volume, scene);
        }
        return new SteadyStateVolume(Volumes.SILENT, scene);
    }

    _getBackgroundSteadyStateVolumeForScene(scene: Scene): SteadyStateVolume {
        /*
        In order of precedence, background audio (music, most likely) should have volume:
            1. if `sceneGroup.musicVolumeOverride` is set, use that volume
            2. if `sceneGroup.voiceoverAsset` or `sceneGroup.mediaAsset` is set, set music volume to 15%
            3. if there is media on the scene that has audio, set background volume to 15%
            4. if there is a voiceover track, set the background volume to 15%
            5. set it to the musicAsset's volume
        */
        const sceneGroup = scene.sceneGroup;

        // 1. check `sceneGroup.musicVolumeOverride
        if (sceneGroup && typeof sceneGroup.musicVolumeOverride === 'number') {
            return new SteadyStateVolume(sceneGroup.musicVolumeOverride, scene, {
                transitionsShouldHappenOutsideSpan: true,
            });
        }

        // 2. check `sceneGroup.voiceoverAsset` and `sceneGroup.mediaAsset`
        if (sceneGroup && (sceneGroup.voiceoverAsset || sceneGroup.mediaAsset)) {
            return new SteadyStateVolume(Volumes.DEFAULT_BACKGROUND_VOLUME, sceneGroup, {
                transitionsShouldHappenOutsideSpan: true,
            });
        }

        // 3. check the background media
        let sceneHasMediaWithVolume = false;
        scene.getAssetsByType(Config.MEDIA_ASSET_TYPE).forEach((mediaAsset) => {
            if (mediaAsset.hasAudio && mediaAsset.volume > 0) {
                sceneHasMediaWithVolume = true;
            }
        });
        if (sceneHasMediaWithVolume) {
            return new SteadyStateVolume(Volumes.DEFAULT_BACKGROUND_VOLUME, scene, {
                transitionsShouldHappenOutsideSpan: true,
            });
        }

        // 4. check the voiceover
        if (this.#getVoiceover()) {
            return new SteadyStateVolume(Volumes.DEFAULT_BACKGROUND_VOLUME, scene);
        }

        // 5. fallback to the musicAsset's volume
        return new SteadyStateVolume(this.#getMusicAsset().volume, scene);
    }

    _getSfxSteadyStateVolumesForScene(scene: Scene): SteadyStateVolume[] {
        return scene.sfx
            .map((sfxItem) => {
                const audioAsset = scene._getSfxAudioAsset(sfxItem.id);
                if (!audioAsset || !audioAsset.sourceData) {
                    return null;
                }

                return new SteadyStateVolume(this.#getSfxVolume(), scene, {
                    asset: audioAsset,
                    isAudioLocal: true,
                    duration: audioAsset.duration,
                });
            })
            .filter((v): v is SteadyStateVolume => v !== null);
    }

    _getAssetSfxSteadyStateVolumesForSceneGroup(sceneGroup: SceneGroup): SteadyStateVolume[] {
        return sceneGroup
            .getAssets()
            .flatMap((asset) =>
                asset.sfx.map((sfxItem) => {
                    const audioAsset = asset._getSfxAudioAsset(sfxItem.id);
                    if (!audioAsset || !audioAsset.sourceData) {
                        return null;
                    }
                    return new SteadyStateVolume(this.#getSfxVolume(), sceneGroup, {
                        asset: audioAsset,
                        isAudioLocal: true,
                        duration: audioAsset.duration,
                    });
                }),
            )
            .filter((v): v is SteadyStateVolume => v !== null);
    }

    /**
     * Given two back-to-back spans, this method will compute where the transition between the spans
     * should occur. There can either be no transition (if the volumes are identical), or the transition
     * should happen during the first span or during the second span.
     * @returns one of NO_TRANSITION, FIRST, or SECOND
     */
    _transitionHappensIn(
        firstSpan: SteadyStateVolume | null,
        secondSpan: SteadyStateVolume | null,
    ): TRANSITION_HAPPENS_IN {
        if (!firstSpan) {
            return TRANSITION_HAPPENS_IN.SECOND;
        }
        if (!secondSpan) {
            return TRANSITION_HAPPENS_IN.FIRST;
        }

        if (firstSpan.volume === secondSpan.volume) {
            return TRANSITION_HAPPENS_IN.NO_TRANSITION;
        }
        if (
            firstSpan.transitionsShouldHappenOutsideSpan &&
            secondSpan.transitionsShouldHappenOutsideSpan
        ) {
            // in this case, both spans prefer to have transitions that happen outside of the span.
            // use the volume to decide where to put the transition, favour keeping the volume lower for longer
            if (firstSpan.volume > secondSpan.volume) {
                return TRANSITION_HAPPENS_IN.FIRST;
            } else {
                return TRANSITION_HAPPENS_IN.SECOND;
            }
        } else if (
            firstSpan.transitionsShouldHappenOutsideSpan &&
            !secondSpan.transitionsShouldHappenOutsideSpan
        ) {
            return TRANSITION_HAPPENS_IN.SECOND;
        } else if (
            !firstSpan.transitionsShouldHappenOutsideSpan &&
            secondSpan.transitionsShouldHappenOutsideSpan
        ) {
            return TRANSITION_HAPPENS_IN.FIRST;
        } else {
            return TRANSITION_HAPPENS_IN.SECOND;
        }
    }

    /**
     * Given two SceneSteadyStateVolumes, this function computes the times when
     * the transition between the two should start and end.
     * @param previous the first SceneSteadyStateVolume
     * @param current the second SceneSteadyStateVolume
     * @param param2 additional context required for the timing calculation: the two SceneSteadyStateVolumes
     * that book-end the scenes between which we are transitioning. For example, if we are calculating the
     * audio transition from Scene 5 -> 6, then this argument should include the SceneSteadyStateVolumes for
     * Scene 4 and Scene 7.
     */
    _computeTransitionTimesBetween(
        previous: SteadyStateVolume,
        current: SteadyStateVolume,
        {
            beforePrevious,
            afterCurrent,
        }: { beforePrevious: SteadyStateVolume | null; afterCurrent: SteadyStateVolume | null },
    ): {
        transitionStartTime: number | undefined;
        transitionEndTime: number | undefined;
    } {
        switch (this._transitionHappensIn(previous, current)) {
            case TRANSITION_HAPPENS_IN.NO_TRANSITION:
                // no transition, so we don't need to do anything
                return { transitionStartTime: undefined, transitionEndTime: undefined };
            case TRANSITION_HAPPENS_IN.FIRST:
                // the transition happens in the previous span.
                if (
                    this._transitionHappensIn(beforePrevious, previous) ===
                    TRANSITION_HAPPENS_IN.SECOND
                ) {
                    /*
                    There are 2 transitions happening in the previous span:

                    1. a transition to the previous span's steady state
                    2. and then a transition to the current span's steady state

                    We will limit the transition into the current span's steady state
                    to only be allowed to occur during the second half of the previous span.
                    */
                    return {
                        transitionStartTime: Math.max(
                            previous.spanMidTime,
                            current.spanBeginningTime - LONG_TRANSITION_DURATION,
                        ),
                        transitionEndTime: current.spanBeginningTime,
                    };
                }
                /*
                The previous span only has the transition to the current span's steady state.
                Allow that transition to occur over the course of the entire previous span.
                */
                return {
                    transitionStartTime: Math.max(
                        previous.spanBeginningTime,
                        current.spanBeginningTime - LONG_TRANSITION_DURATION,
                    ),
                    transitionEndTime: current.spanBeginningTime,
                };
            case TRANSITION_HAPPENS_IN.SECOND: {
                const transitionStartTime = Math.min(previous.spanEndTime, current.spanMidTime);

                // We need to check if there is a transition at the end of the current span as well
                if (
                    (!!afterCurrent &&
                        this._transitionHappensIn(current, afterCurrent) ===
                            TRANSITION_HAPPENS_IN.FIRST) || // there is a transition happening into the next span
                    afterCurrent === null // this is the last span, in which case we know that there will also be a transition to 0 at the end
                ) {
                    /*
                    Similar to above, there are 2 transitions happening in the current span:

                    1. a transition to the current span's steady state
                    2. and then a transition to the next span's steady state

                    We will limit the transition into the current span's steady state
                    to only be allowed to occur during the first half of the current span.
                    */
                    return {
                        transitionStartTime,
                        transitionEndTime: Math.min(
                            current.spanMidTime,
                            transitionStartTime + LONG_TRANSITION_DURATION,
                        ),
                    };
                }
                /*
                Similar to above, the current span only has the transition to the current span's steady
                state. Allow that transition to occur over the course of the enture current span.
                */
                return {
                    transitionStartTime,
                    transitionEndTime: Math.min(
                        current.spanEndTime,
                        transitionStartTime + LONG_TRANSITION_DURATION,
                    ),
                };
            }
        }
    }

    /**
     * All of our AudioAssets, except the background audio, have a pretty simple volume curve:
     *
     *  - go from 0 to the full volume at the beginning
     * - go from full volume to 0 at the end
     *
     * This method generates those transitionPoints
     * @returns transitionPoints for the AudioAsset
     */
    _computeSimpleTransitionPoints(steadyStateVolumes: SteadyStateVolume[]) {
        const beginning = steadyStateVolumes[0];
        const end = steadyStateVolumes[steadyStateVolumes.length - 1];

        const entranceStartTime = Math.max(
            beginning.spanBeginningTime - QUICK_TRANSITION_DURATION,
            0,
        );
        const entranceEndTime = entranceStartTime + QUICK_TRANSITION_DURATION;
        const exitStartTime = end.spanEndTime - QUICK_TRANSITION_DURATION;
        const exitEndTime = end.spanEndTime;

        if (exitStartTime < entranceStartTime) {
            return [];
        }

        return [
            // create the transition in at the beginning
            new VolumePoint(0, entranceStartTime),
            new VolumePoint(beginning.volume, entranceEndTime),

            // create the transition out at the end
            new VolumePoint(end.volume, exitStartTime),
            new VolumePoint(0, exitEndTime),
        ];
    }

    /**
     * Our fanciest audio track is the background audio. This method converts the SceneSteadyStateVolumes
     * from the background audio track to the transitionPoints that will be passed into the background track's
     * AudioAsset
     * @returns transitionPoints for the background track's AudioAsset
     */
    _computeBackgroundTransitionPoints(steadyStateVolumes: SteadyStateVolume[]) {
        const transitionPoints = [];
        for (let i = 0; i < steadyStateVolumes.length; i++) {
            const previous = steadyStateVolumes[i - 1] || null;
            const current = steadyStateVolumes[i];
            const next = steadyStateVolumes[i + 1] || null;

            const isStart = previous === null;
            const isEnd = next === null;

            if (isStart) {
                // always start from 0, and then transition as quickly as possible to the new volume
                const transitionStartTime = Math.max(
                    current.spanBeginningTime - LONG_TRANSITION_DURATION,
                    0,
                );
                transitionPoints.push(new VolumePoint(0, transitionStartTime));
                const transitionEndTime = Math.min(
                    transitionStartTime + LONG_TRANSITION_DURATION,
                    current.spanMidTime,
                );
                transitionPoints.push(new VolumePoint(current.volume, transitionEndTime));
            } else {
                const { transitionStartTime, transitionEndTime } =
                    this._computeTransitionTimesBetween(previous, current, {
                        beforePrevious: steadyStateVolumes[i - 2] || null,
                        afterCurrent: next,
                    });

                if (transitionStartTime !== undefined && transitionEndTime !== undefined) {
                    // there was a transition. Add the transition's points to the transitionPoints array
                    transitionPoints.push(new VolumePoint(previous.volume, transitionStartTime));
                    transitionPoints.push(new VolumePoint(current.volume, transitionEndTime));
                }
            }

            if (isEnd) {
                const transitionStartTime = Math.max(
                    current.spanMidTime,
                    current.spanEndTime - LONG_TRANSITION_DURATION,
                );
                transitionPoints.push(new VolumePoint(current.volume, transitionStartTime));
                const transitionEndTime = Math.min(
                    current.spanEndTime,
                    transitionStartTime + LONG_TRANSITION_DURATION,
                );
                transitionPoints.push(new VolumePoint(0, transitionEndTime));
            }
        }
        return transitionPoints;
    }

    get _hasBackgroundAudio() {
        const musicId = this.#getMusicId();
        return !!musicId && !!this.#getAudioAssets()[musicId];
    }

    get _hasVoiceover() {
        const voiceoverId = this.#getVoiceoverId();
        return !!voiceoverId && !!this.#getAudioAssets()[voiceoverId];
    }

    /**
     * The main method that gets called to update everything.
     */
    update() {
        AudioAnimationManager._applyTransitionPoints(this._computeTransitionPoints());
    }

    /** @internal */
    static _applyTransitionPoints(assetTransitionPoints: readonly AssetTransitionPoints[]) {
        assetTransitionPoints.forEach(({ asset, transitionPoints }) => {
            asset.transitionPoints = transitionPoints;
        });
    }

    /**
     * The volume transition points every animated audio asset should have, computed without writing them.
     * @internal
     */
    _computeTransitionPoints(): AssetTransitionPoints[] {
        // get steady state volumes
        const mediaAssetsSteadyStateVolumes: SteadyStateVolume[] = [];
        const sfxSteadyStateVolumes: SteadyStateVolume[] = [];
        const voiceoverSteadyStateVolumes: SteadyStateVolume[] = [];
        const backgroundSteadyStateVolumes: SteadyStateVolume[] = [];

        this.#getSceneGroups().forEach((sceneGroup) => {
            mediaAssetsSteadyStateVolumes.push(
                ...this._getMediaAssetSteadyStateVolumesForSceneGroup(sceneGroup),
            );
            mediaAssetsSteadyStateVolumes.push(
                ...this._getVoiceoverAssetSteadyStateVolumesForSceneGroup(sceneGroup),
            );
            sfxSteadyStateVolumes.push(
                ...this._getAssetSfxSteadyStateVolumesForSceneGroup(sceneGroup),
            );
            sceneGroup._scenes.forEach((scene) => {
                mediaAssetsSteadyStateVolumes.push(
                    ...this._getMediaAssetSteadyStateVolumesForScene(scene),
                );
                sfxSteadyStateVolumes.push(...this._getSfxSteadyStateVolumesForScene(scene));
                if (this._hasVoiceover) {
                    voiceoverSteadyStateVolumes.push(
                        this._getVoiceoverSteadyStateVolumeForScene(scene),
                    );
                }
                if (this._hasBackgroundAudio) {
                    backgroundSteadyStateVolumes.push(
                        this._getBackgroundSteadyStateVolumeForScene(scene),
                    );
                }
            });
        });

        return [
            ...(this._hasVoiceover
                ? [
                      {
                          asset: this.#getAudioAssets()[this.#getVoiceoverId()],
                          transitionPoints: this._computeSimpleTransitionPoints(
                              voiceoverSteadyStateVolumes,
                          ),
                      },
                  ]
                : []),
            ...(this._hasBackgroundAudio
                ? [
                      {
                          asset: this.#getAudioAssets()[this.#getMusicId()],
                          transitionPoints: this._computeBackgroundTransitionPoints(
                              backgroundSteadyStateVolumes,
                          ),
                      },
                  ]
                : []),
            ...[...mediaAssetsSteadyStateVolumes, ...sfxSteadyStateVolumes].flatMap(
                (steadyStateVolume) =>
                    steadyStateVolume.asset
                        ? [
                              {
                                  asset: steadyStateVolume.asset,
                                  transitionPoints: this._computeSimpleTransitionPoints([
                                      steadyStateVolume,
                                  ]),
                              },
                          ]
                        : [],
            ),
        ];
    }
}
